import React, { createContext, useContext, useState, useEffect } from 'react';
import novaConversationService from '../services/novaConversationService';
import { useAuth } from './AuthContext';

const NovaChatContext = createContext();

export const useNovaChat = () => {
  const context = useContext(NovaChatContext);
  if (!context) {
    // Return default values during initialization
    return {
      isOpen: false,
      messages: [],
      isTyping: false,
      unreadCount: 0,
      openChat: () => {},
      closeChat: () => {},
      toggleChat: () => {},
      sendMessage: () => {},
      clearChat: () => {},
      executeAction: () => {}
    };
  }
  return context;
};

export const NovaChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const { user, profile } = useAuth();

  // Load conversation history from database on mount
  useEffect(() => {
    const loadHistory = async () => {
      if (!user || messages.length > 0 || isLoadingHistory) return;
      
      setIsLoadingHistory(true);
      try {
        const history = await novaConversationService.getConversationHistory(user.id);
        
        if (history && history.length > 0) {
          // Convert database format to UI format
          const formattedMessages = history.flatMap((msg, idx) => [
            {
              id: `user-${idx}`,
              type: 'user',
              message: msg.user_message,
              timestamp: new Date(msg.timestamp)
            },
            {
              id: `nova-${idx}`,
              type: 'nova',
              message: msg.nova_response,
              actions: msg.actions || [],
              timestamp: new Date(msg.timestamp)
            }
          ]);
          
          setMessages(formattedMessages);
        } else {
          // No history - show welcome message
          const welcomeMessage = {
            id: 'welcome',
            type: 'nova',
            message: "Hey! I'm Nova, your UGC creator coach. 🎬\n\nI can help you with scripts, hooks, brand deals, and social media strategy. What are you working on?",
            actions: [
              { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
              { label: 'Find Brands', type: 'navigate', path: '/app/ugc-brands' },
              { label: 'Ask a Question', type: 'focus_input' }
            ],
            timestamp: new Date()
          };
          setMessages([welcomeMessage]);
        }
      } catch (error) {
        console.error('Error loading Nova chat history:', error);
        // Show welcome message on error
        const welcomeMessage = {
          id: 'welcome',
          type: 'nova',
          message: "Hey! I'm Nova, your UGC creator coach. 🎬\n\nI can help you with scripts, hooks, brand deals, and social media strategy. What are you working on?",
          actions: [
            { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
            { label: 'Find Brands', type: 'navigate', path: '/app/ugc-brands' },
            { label: 'Ask a Question', type: 'focus_input' }
          ],
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [user, messages.length, isLoadingHistory]);

  const openChat = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  const toggleChat = () => {
    if (isOpen) {
      closeChat();
    } else {
      openChat();
    }
  };

  const sendMessage = async (messageText, currentPath = '') => {
    if (!messageText.trim() || !user) return;

    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      message: messageText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Show typing indicator
    setIsTyping(true);

    try {
      // Get Nova response
      const context = {
        currentPath,
        userId: user.id,
        profileData: profile
      };

      const response = await novaConversationService.sendMessage(messageText, context);

      // Add Nova response
      const novaMessage = {
        id: `nova-${Date.now()}`,
        type: 'nova',
        message: response.message,
        actions: response.actions || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, novaMessage]);

      // Increment unread if chat is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message to Nova:', error);
      
      const errorMessage = {
        id: `nova-${Date.now()}`,
        type: 'nova',
        message: "Oops! I'm having trouble right now. Can you try that again?",
        actions: [
          { label: 'Try Again', type: 'focus_input' }
        ],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const executeAction = (action) => {
    if (!action) return;

    switch (action.type) {
      case 'navigate':
        window.location.href = action.path;
        break;

      case 'focus_input':
        // Handled by UI component
        break;

      case 'custom':
        handleCustomAction(action.action);
        break;

      default:
        console.warn('Unknown action type:', action.type);
    }
  };

  const handleCustomAction = async (actionName) => {
    // Handle custom actions with Nova responses
    setIsTyping(true);

    try {
      let response;

      switch (actionName) {
        case 'show_hook_tips':
          response = {
            message: "**Hook Formula:**\n\n1. Pattern interrupt (POV, Stop, I tried)\n2. Promise value (so you don't have to, honest review)\n3. Create urgency (before your next scroll)\n\n**Pro tips:**\n• Test 3 hooks per video\n• Front-load in first 2s\n• Use on-screen text\n• Match platform tone",
            actions: [
              { label: 'Generate Custom Hooks', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
          break;

        case 'show_cta_examples':
          response = {
            message: "**Strong CTAs:**\n\n🔗 Link in bio (classic)\n💬 DM me 'START' (engagement)\n📲 Click the link above (direct)\n💾 Save this for later (bookmark)\n👉 Try it yourself (action)\n🎁 Use code XYZ (conversion)\n\nMatch CTA to goal: awareness → save, conversion → click",
            actions: [
              { label: 'Build Script with CTA', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
          break;

        case 'show_pricing_guide':
          response = {
            message: "**Rate Card Builder:**\n\n💰 Base factors:\n• Follower count\n• Usage rights (organic vs paid)\n• Exclusivity\n• Deliverables\n• Revisions\n\n📊 Tier examples:\n1-10k followers: $300-700/video\n10-50k: $700-1500\n50k+: $1500-3000+\n\nAlways offer packages (3-5 videos) for better LTV.",
            actions: [
              { label: 'Build My Packages', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
          break;

        case 'show_outreach_templates':
          response = {
            message: "**Outreach Template:**\n\nSubject: Quick UGC concept for [Brand]\n\nHey [Name],\nI shot a concept for [Product] aimed at [Audience]. I can deliver 3x [Platform]-ready videos with raw cuts and paid usage.\n\nIdeas:\n• Hook: [Your hook]\n• Proof: [Social proof]\n• CTA: [Their CTA]\n\nPackage: 3 videos + raw + 30-day usage\nWant a sample this week?\n\n[Your name]",
            actions: [
              { label: 'Browse Brands', type: 'navigate', path: '/app/ugc-brands' }
            ]
          };
          break;

        case 'show_all_platform_tips':
          response = {
            message: "**Platform Playbook:**\n\n🎵 TikTok: 9-15s, trending audio, fast cuts\n📷 IG Reels: 15-30s, cover safe zones, strong first frame\n▶️ YouTube Shorts: 30-45s, clear VO, end screen CTA\n\n**Universal:**\n• Captions (85% watch silent)\n• Hook in 1-2s\n• Fast pacing\n• Clear CTA",
            actions: [
              { label: 'Generate Platform Script', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
          break;

        case 'show_content_ideas':
          response = {
            message: "**Content Ideas:**\n\n📊 Proven formats:\n• Before/After\n• Day in the life\n• Honest review\n• 3 tips/hacks\n• POV scenarios\n• Problem → Solution\n• Storytime + product\n\n🔄 Repurpose strategy:\nSame script → 3 platforms → 3 hooks = 9 pieces",
            actions: [
              { label: 'Generate Scripts', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
          break;

        default:
          response = {
            message: "I'm not sure about that one. Want to try the script generator?",
            actions: [
              { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' }
            ]
          };
      }

      const novaMessage = {
        id: `nova-${Date.now()}`,
        type: 'nova',
        message: response.message,
        actions: response.actions || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, novaMessage]);
    } catch (error) {
      console.error('Error handling custom action:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const clearChat = async () => {
    try {
      if (user) {
        await novaConversationService.clearHistory(user.id);
      }
      
      const welcomeMessage = {
        id: 'welcome',
        type: 'nova',
        message: "Chat cleared! Ready for a fresh start. What do you want to create?",
        actions: [
          { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
          { label: 'Find Brands', type: 'navigate', path: '/app/ugc-brands' }
        ],
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Error clearing Nova chat:', error);
    }
  };

  const value = {
    isOpen,
    messages,
    isTyping,
    unreadCount,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearChat,
    executeAction
  };

  return (
    <NovaChatContext.Provider value={value}>
      {children}
    </NovaChatContext.Provider>
  );
};

export default NovaChatContext;
